import { forwardRef, KeyboardEvent } from 'react';

import { iosSafeInputText } from '@/styles/input';

import { SearchIcon } from '@/ui/Icons/SearchIcon';
import { XIcon } from '@/ui/Icons/XIcon';

import { classNames } from '../../../../utils/classnames';

type Props = {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  /**
   * Placeholder text shown when the input is empty. Also used as the
   * accessible name so screen-reader users hear the same context.
   * Defaults to "Search token".
   */
  placeholder?: string;
};

const DEFAULT_PLACEHOLDER = 'Search token';

/**
 * Search input rendered inside the discovery table's "Token" column header.
 * Used in both the regular and sticky overlay headers; the parent owns the
 * search state and clear handler so both inputs stay in sync.
 *
 * The input is intentionally a raw `<input>` (no `src/ui` primitive matches
 * the borderless, header-cell look) and the icon column lives inside the
 * `<label>` so the whole label is a click target.
 */
export const TokenSearchInput = forwardRef<HTMLInputElement, Props>(function TokenSearchInput(
  { value, onChange, onClear, placeholder = DEFAULT_PLACEHOLDER },
  ref,
) {
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      // Focus management is delegated entirely to the parent's `onClear`,
      // which focuses whichever search input is currently visible. Do not
      // blur here, otherwise we'd cancel that focus and Escape would behave
      // inconsistently with the X button.
      onClear();
      return;
    }
    // The Matcha header has a global `/` shortcut that focuses the top search
    // bar. Stop just that key from bubbling so users can type `/` while
    // searching tokens here.
    if (event.key === '/') {
      event.stopPropagation();
    }
  };

  return (
    <label className="flex items-center gap-2 w-full text-token-gray-800 focus-within:text-token-gray-1000">
      <SearchIcon className="size-4 shrink-0" />
      <input
        ref={ref}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={placeholder}
        maxLength={100}
        className={classNames(
          'flex-1 min-w-0 bg-transparent border-0 p-0 outline-none',
          'font-inherit placeholder:text-token-gray-800',
          iosSafeInputText.sm,
        )}
      />
      {value.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear search"
          className={classNames(
            'shrink-0 rounded-full p-0.5 text-token-gray-800',
            'hover:text-token-gray-1600 focus-visible:outline-[3px]',
            'focus-visible:outline-token-gray-500 focus-visible:outline-solid',
          )}
        >
          <XIcon className="size-3" />
        </button>
      )}
    </label>
  );
});
